import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { NgApexchartsModule } from 'ng-apexcharts';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { DashboardService } from '../../../core/services/dashboard.service';
import { ProcessByStatus, ProcessesByStatusResponse } from '../../../core/models/dashboard.model';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-ui-processes-by-status',
  standalone: true,
  imports: [CommonModule, NgApexchartsModule, LoadingSpinnerComponent],
  templateUrl: './ui-processes-by-status.component.html'
})
export class UiProcessesByStatusComponent implements OnInit, OnDestroy {
  public processesByStatus: ProcessByStatus[] = [];
  public loading = false;
  public error = false;
  public chartOptions: any;
  public hasData = false;

  private destroy$ = new Subject<void>();

  constructor(
    private dashboardService: DashboardService,
    private authService: AuthService
  ) {
    // Não inicializa o gráfico aqui - será inicializado apenas quando houver dados
  }

  ngOnInit(): void {
    this.loadProcessesByStatus();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public loadProcessesByStatus(): void {
    // Verificar se o usuário está autenticado
    if (!this.authService.isAuthenticated()) {
      console.warn('Usuário não autenticado. Não é possível carregar processos por status.');
      this.error = true;
      return;
    }

    this.loading = true;
    this.error = false;

    this.dashboardService.getProcessesByStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.processesByStatus = response.data;
            this.hasData = false;
            
            // Só atualiza o gráfico se houver processos E se houver dados relevantes
            if (response.data.length > 0) {
              // Verifica se pelo menos um status tem processos
              const hasProcessesInStatus = response.data.some(process => process.count > 0);
              
              if (hasProcessesInStatus) {
                this.hasData = true;
                this.updateChartData(response.data);
              }
            }
            
            this.loading = false;
          } else {
            console.warn('Resposta da API sem dados válidos:', response);
            this.hasData = false;
            this.error = true;
            this.loading = false;
          }
        },
        error: (error) => {
          console.error('Erro ao carregar processos por status:', error);
          this.hasData = false;
          this.handleApiError(error);
          this.loading = false;
        }
      });
  }

  /**
   * Atualiza os dados do gráfico com base nos processos por status recebidos
   * @param processesByStatus Lista de processos por status da API
   */
  private updateChartData(processesByStatus: ProcessByStatus[]): void {
    const data = processesByStatus.map(process => process.count);
    const labels = processesByStatus.map(process => process.status);
    const colors = processesByStatus.map(process => this.getStatusColor(process.status));

    this.chartOptions = {
      series: data,
      chart: {
        height: 335,
        type: 'pie',
        toolbar: {
          show: false,
        },
      },
      colors: colors,
      labels: labels,
      legend: {
        position: 'bottom',
        fontSize: '12px',
        fontWeight: 500,
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
        },
        dropShadow: {
          enabled: false,
        },
      },
      plotOptions: {
        pie: {
          expandOnClick: true,
          donut: {
            size: '0%',
          },
        },
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val + ' processos';
          },
        },
      },
    };
  }

  /**
   * Retorna a cor específica para cada status seguindo o padrão do Tailwind
   * @param status Status do processo
   * @returns Código hexadecimal da cor
   */
  private getStatusColor(status: string): string {
    const statusColorMap: { [key: string]: string } = {
      'Concluído': '#10b981',        // bg-success - Verde sólido
      'Em andamento': '#f59e0b',     // bg-warning - Amarelo/Laranja sólido
      'A iniciar': '#49B6F5',        // Azul claro customizado
      'Fechado': '#ef4444',          // Vermelho - Processo encerrado manualmente
    };

    return statusColorMap[status] || '#6b7280'; // Cinza sólido como fallback
  }

  /**
   * Trata erros específicos da API
   * @param error Erro recebido da API
   */
  private handleApiError(error: any): void {
    this.error = true;
    
    // Log específico baseado no tipo de erro
    if (error.status === 401) {
      console.warn('Token não fornecido, inválido ou expirado');
    } else if (error.status === 500) {
      console.error('Erro interno do servidor');
    } else {
      console.error('Erro desconhecido ao carregar processos por status:', error);
    }
  }
}
